import type { AudioProvider, Track, PlaybackState, PlaybackListener } from "../types/audio";

/**
 * Fallback audio provider for self-hosted clips.
 *
 * Use this for tracks not available on iTunes, or when you have
 * licensed clips hosted on your own CDN / Supabase Storage.
 *
 * Same Web Audio API approach as ITunesProvider for consistent timing.
 */
export class SelfHostedProvider implements AudioProvider {
  // @ts-ignore used for future CDN resolution
  private _baseUrl: string;
  private audioContext: AudioContext | null = null;
  private audioBuffer: AudioBuffer | null = null;
  private sourceNode: AudioBufferSourceNode | null = null;
  private state: PlaybackState = { status: "idle", currentTime: 0, duration: 0 };
  private listeners: Set<PlaybackListener> = new Set();
  private progressInterval: ReturnType<typeof setInterval> | null = null;
  private playbackStartedAt = 0;
  private clipStartTime = 0;
  private clipDuration = 0;

  /**
   * @param baseUrl - Base URL for your audio hosting (e.g., Supabase Storage bucket URL)
   */
  constructor(baseUrl: string) {
    this._baseUrl = baseUrl.replace(/\/$/, "");
  }

  async search(_artist: string, _title: string): Promise<Track | null> {
    // Self-hosted provider doesn't search — tracks are resolved by the
    // game engine which knows the clip filename. Return null to signal
    // that upstream should provide the Track object directly.
    return null;
  }

  async load(track: Track): Promise<void> {
    this.stop();
    this.setState({ status: "loading", currentTime: 0, duration: 0 });

    if (!this.audioContext) {
      this.audioContext = new AudioContext();
    }

    const resp = await fetch(track.previewUrl);
    if (!resp.ok) throw new Error(`Failed to load audio: ${resp.status}`);

    const arrayBuffer = await resp.arrayBuffer();
    this.audioBuffer = await this.audioContext.decodeAudioData(arrayBuffer);

    this.setState({
      status: "idle",
      currentTime: 0,
      duration: this.audioBuffer.duration,
    });
  }

  async playClip(startSec: number, durationSec: number): Promise<void> {
    if (!this.audioContext || !this.audioBuffer) {
      throw new Error("No track loaded. Call load() first.");
    }

    this.stopSource();

    if (this.audioContext.state === "suspended") {
      await this.audioContext.resume();
    }

    const maxStart = Math.max(0, this.audioBuffer.duration - durationSec);
    const clampedStart = Math.min(startSec, maxStart);

    this.clipStartTime = clampedStart;
    this.clipDuration = durationSec;

    this.sourceNode = this.audioContext.createBufferSource();
    this.sourceNode.buffer = this.audioBuffer;
    this.sourceNode.connect(this.audioContext.destination);
    this.sourceNode.start(0, clampedStart, durationSec);
    this.playbackStartedAt = this.audioContext.currentTime;

    this.setState({ status: "playing", currentTime: clampedStart, duration: this.audioBuffer.duration });

    this.progressInterval = setInterval(() => {
      if (!this.audioContext || this.state.status !== "playing") return;
      const elapsed = this.audioContext.currentTime - this.playbackStartedAt;
      this.setState({ ...this.state, status: "playing", currentTime: this.clipStartTime + elapsed });
    }, 50);

    this.sourceNode.onended = () => {
      this.clearProgress();
      this.setState({ ...this.state, status: "ended", currentTime: this.clipStartTime + this.clipDuration });
    };
  }

  pause(): void {
    if (this.audioContext?.state === "running") {
      this.audioContext.suspend();
      this.clearProgress();
      this.setState({ ...this.state, status: "paused" });
    }
  }

  stop(): void {
    this.stopSource();
    this.clearProgress();
    this.audioBuffer = null;
    this.setState({ status: "idle", currentTime: 0, duration: 0 });
  }

  onStateChange(listener: PlaybackListener): () => void {
    this.listeners.add(listener);
    return () => this.listeners.delete(listener);
  }

  getState(): PlaybackState {
    return { ...this.state };
  }

  getAudioBuffer(): AudioBuffer | null {
    return this.audioBuffer;
  }

  private stopSource(): void {
    try { this.sourceNode?.stop(); } catch { /* already stopped */ }
    this.sourceNode?.disconnect();
    this.sourceNode = null;
    if (this.audioContext?.state === "suspended") this.audioContext.resume();
  }

  private clearProgress(): void {
    if (this.progressInterval) {
      clearInterval(this.progressInterval);
      this.progressInterval = null;
    }
  }

  private setState(newState: PlaybackState): void {
    this.state = newState;
    for (const listener of this.listeners) listener({ ...this.state });
  }
}
